/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { TableHead, TableRow, TableBody, MenuItem } from '@mui/material';
import { format, differenceInMinutes } from 'date-fns';
import {
  GeneralTable,
  CustomTableCell,
  CustomTableRow,
  AvatarTableCell,
  SelectDropdown,
} from '../../index';
import { useEffect, useState } from 'react';
import { getSessionLunchWindow } from '../../../utilities/lunchWindow';

export const ClockInTable = (props) => {
  const activeEmpData = props.activeEmpData;
  const [entryPerPage, setEntryPerPage] = useState(10);
  const [slice, setSlice] = useState([0, entryPerPage]);
  const workSetup = { 1: 'On-site', 2: 'WFH', 3: 'Deployed' };
  const [currentPage, setCurrentPage] = useState(1);

  const checkInTime = props.timesheetActive.filter((data) => {
    return data.checkout === null;
  });

  //Reset to page 1 when changing the number of entries
  useEffect(() => {
    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  }, [entryPerPage]);

  const data = activeEmpData.map((emp) => ({
    ...emp,
    ...checkInTime.find((time) => time.empid === emp._id),
    // Current worked hours is computed here
    hours: (() => {
      const checkInObj = checkInTime.find((time) => time.empid === emp._id);
      const checkIn = checkInObj ? new Date(checkInObj.checkin) : new Date();
      const currentTime = new Date();

      let rawtime, roundedtime, currentWorkedHours;

      const { lunchStart, lunchEnd } = getSessionLunchWindow(
        checkIn,
        checkInObj?.worksetup,
        checkInObj?.lunchstart
      );

      // check if lunch time is within checkIn and checkout time
      if (checkIn < lunchStart && currentTime > lunchEnd) {
        // deduct one hour from time elapsed
        rawtime = currentTime - checkIn - 3.6e6;
      }
      // check in is before lunch, and it is lunchbreak
      else if (
        checkIn < lunchStart &&
        currentTime >= lunchStart &&
        currentTime <= lunchEnd
      ) {
        rawtime = lunchStart - checkIn;
      }
      // check if check in time is during lunch, and lunch has not yet ended
      else if (checkIn >= lunchStart && currentTime <= lunchEnd) {
        rawtime = 0;
      }
      // check if checkIn time is within lunch time, current time is past lunch
      else if (checkIn >= lunchStart && checkIn <= lunchEnd) {
        // actual work hours is only from current time to lunch End
        rawtime = currentTime - lunchEnd;
      }
      // other cases, return actual time elapsed (both checkin and current time are before lunch or after lunch)
      else {
        rawtime = currentTime - checkIn;
      }

      // Get time in minutes considering seconds
      roundedtime = Math.floor((rawtime / 60000) * 1000) / 1000;
      currentWorkedHours = Math.floor((roundedtime / 60) * 1000) / 1000;

      return currentWorkedHours;
    })(),
    ...props.departmentList.find((dept) => emp.deptid === dept._id),
  }));

  // PAGE CHANGE HANDLER
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;

    page === 1
      ? setSlice([0, entryPerPage])
      : setSlice([currPage * entryPerPage, page * entryPerPage]);
  };

  return (
    <GeneralTable
      sx={{
        borderRadius: '5px',
        boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
        mb: 3.6,
        display: 'block',
      }}
      length={data.length}
      entryPerPage={entryPerPage}
      page={currentPage}
      pageCount={Math.floor(data.length / entryPerPage)}
      handlePageChange={handlePageChange}
      entriesPerPageComponent={
        <SelectDropdown
          sx={{ minWidth: 80 }}
          size="small"
          label="Entries"
          value={entryPerPage}
          onChange={(e) => setEntryPerPage(e.target.value)}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </SelectDropdown>
      }
    >
      <TableHead>
        <TableRow>
          <CustomTableCell align="left" value="Employee" />
          <CustomTableCell align="left" value="Check In Date" />
          <CustomTableCell align="left" value="Check In Time" />
          <CustomTableCell align="left" value="Current Worked Hours" />
          <CustomTableCell value="Work Setup" />
          <CustomTableCell value="Department" />
          <CustomTableCell value="Project" />
        </TableRow>
      </TableHead>
      <TableBody>
        {data.slice(slice[0], slice[1]).map((emp, index) => (
          <CustomTableRow key={index}>
            <AvatarTableCell
              scope="row"
              value={`${emp.firstname} ${emp.lastname}`}
            />
            <CustomTableCell
              scope="row"
              value={format(new Date(emp.lastlogindate), 'dd MMM yyyy')}
            />
            <CustomTableCell
              scope="row"
              value={format(new Date(emp.lastlogindate), 'hh:mm:ss a')}
            />
            <CustomTableCell scope="row" value={emp.hours} />
            <CustomTableCell scope="row" value={workSetup[emp.worksetup]} />
            <CustomTableCell scope="row" value={emp.deptname} />
            <CustomTableCell scope="row" value="" />
          </CustomTableRow>
        ))}
      </TableBody>
    </GeneralTable>
  );
};
